import React from 'react';
import Section from './project-section';
import original from '../media/rl-robot/original.gif';
import envEx from '../media/rl-robot/env_ex.gif';
import obsEx from '../media/rl-robot/obs_ex.png';
import rewardEx from '../media/rl-robot/reward_ex.png';
import trainTime from '../media/rl-robot/train_time.png';
import nonrandom0 from '../media/RL_robot.gif';
import nonrandom02 from '../media/rl-robot/nonrandom_02.gif';
import randomNn from '../media/rl-robot/random_nn.gif';
import setup from '../media/rl-robot/setup.png';
import model from '../media/rl-robot/model2.gif';
import calibrate from '../media/rl-robot/calibrate.gif';
import calibResults from '../media/rl-robot/calib_results.png';

const fetchPush = [{
  src: original,
  alt: 'OpenAI FetchPush-v1 robot pushing a block',
  caption: <a href="https://gym.openai.com/envs/FetchPush-v1/">FetchPush-v1</a>,
}];

const RLRobot = () => {
  return (
    <>
      <Section title="Task" figures={fetchPush}>
        <p>
          This project was an experiment in transferring a reinforcement learning agent from a simulation to a
          physical robot. My choice of task was inspired an existing OpenAI gym environment for a robotic manipulator
          pushing a block towards a target
          (<a href="https://gym.openai.com/envs/FetchPush-v1/">FetchPush-v1</a>).
        </p>
      </Section>

      <Section figures={[{ src: envEx, alt: 'simulated end effector pushing a puck towards a goal' }]}>
        <p>This OpenAI environment consists of a robotic manipulator</p>
        <ul>
          <li>Controlled by joint velocities</li>
          <li>Operating in three dimensions</li>
          <li>Pushing a cubic block towards a goal</li>
        </ul>
        <p>The environment I chose to implement differs from this in a few key ways. My implementation has the robotic manipulator</p>
        <ul>
          <li>Operating in only two dimensions, x and y</li>
          <li>Controlled only by displacement (velocity) of the end effector</li>
          <li>Pushing a cylindrical &quot;puck&quot; towards a goal</li>
        </ul>
        <p>
          For this project I implemented the puck task as an OpenAI gym environment, utilizing Pybullet for the physics
          simulation.
        </p>
        <p>Here&apos;s an example agent using the end effector (red) to push the puck (blue) towards the goal (yellow).</p>
      </Section>

      <Section title="Agent Observation" figures={[{ src: obsEx, alt: 'observation vectors from end effector to puck and goal' }]}>
        <p>
          The input, or observation for the agent is two 2-dimensional unit vectors. The first vector represents the
          direction of the puck relative to the end effector. The second vector represents the direction of the goal
          relative to the end effector.
        </p>
      </Section>

      <Section title="Agent Action">
        <p>
          The output/action of the agent is a single 2-dimensional vector representing the horizontal and vertical
          displacement that the end effector should undertake. In the simulation this was implemented as setting velocities
          over a constant time interval.
        </p>
      </Section>

      <Section title="Agent Reward" figures={[{ src: rewardEx, alt: 'reward components diagram' }]}>
        <p>The reward for each step is calculated with three components</p>
        <ol>
          <li>When the agent moves the puck closer to the goal, reward increases significantly</li>
          <li>When the agent moves the end effector closer to the puck, reward is increased a small amount</li>
          <li>Each timestep, a small reward is subtracted to incentivize efficient action</li>
        </ol>
      </Section>

      <Section title="Environment Variation">
        <p>
          The environment has the option to initialize randomly, with the goal and puck positions in a randomly selected
          direction from the center.
        </p>
        <p>
          The environment also has the option to add random noise to observations and actions. A random noise factor of
          .05 means that all observation and displacement values have added gaussian noise with a standard deviation of
          5%.
        </p>
      </Section>

      <Section title="Virtual Results">
        <p>
          For this reinforcement learning task I utilized the Proximal Policy Optimization (PPO) algorithm from
          the <a href="https://stable-baselines.readthedocs.io/en/master/">stable baselines</a> repository.
        </p>
        <p>The virtual agent was trained and tested in the following virtual environments:</p>
        <ul>
          <li>Non-random goal position, no added noise</li>
          <li>Non-random goal position, .01 added noise</li>
          <li>Non-random goal position, .02 added noise</li>
          <li>Non-random goal position, .05 added noise</li>
          <li>Random goal position, no added noise</li>
          <li>Random goal position, .01 added noise</li>
        </ul>
      </Section>

      <Section title="Training Times" figures={[{ src: trainTime, alt: 'episode reward over training timesteps' }]}>
        <p>
          Training times are measured in total timesteps trained. Episodes are forcefully ended after 20 timesteps, so there
          are at least timesteps/20 episodes to reach given performance. Recall that the agent earns large rewards by
          progressing the puck towards the goal, and small rewards moving the end effector towards the puck.
        </p>
      </Section>

      <Section>
        <p>
          Here we can see several trends emerge. First, at around 300,000 timesteps the agent learns to reliably move towards
          the puck, gaining small rewards. At around 1 million timesteps the non-random goal environments start to learn to push the
          puck towards the goal, earning larger and larger rewards. Unfortunately, the high-noise and random-goal environments
          never make it past the first stage, and end up with stagnating rewards.
        </p>
      </Section>

      <Section title="Agent Behavior" figures={[{ src: nonrandom0, alt: 'agent in non-random, no noise environment', caption: 'Non-random, no noise' }]} />

      <Section>
        <p>
          <strong>Non-random, no noise:</strong> the agent has developed a strategy of small incremental steps, efficiently moving the puck
          towards the goal. It doesn&apos;t always maintain contact with the puck, but it still works quite quickly.
        </p>
      </Section>

      <Section figures={[{ src: nonrandom02, alt: 'agent in non-random, .02 noise environment', caption: 'Non-random, .02 noise' }]} />

      <Section>
        <p>
          <strong>Non-random, .02 noise:</strong> similarly to the last environment, the agent moves in small steps to incrementally advance the puck. Unlike the
          no-noise version however, this agent experiences more slip-ups and mistakes. When the agent cannot precisely know
          where the puck is, and the actions have random noise, the agent will struggle to be as efficient.
        </p>
      </Section>

      <Section figures={[{ src: randomNn, alt: 'agent in random, no noise environment', caption: 'Random, no noise' }]} />

      <Section>
        <p>
          <strong>Random, no noise:</strong> while the agent moves closer to the puck, it is not ultimately successful in effectively
          pushing the puck to the goal. This lines up with the predictions from earlier, that the agent gets minor rewards,
          but cannot fully execute the task.
        </p>
      </Section>

      <Section title="End Performance">
        <p>After training for over 6 million timesteps, here are the results:</p>
        <table>
          <thead>
            <tr>
              <th>Environment</th>
              <th>Mean Episode Reward</th>
              <th>Task Success Rate</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Non-random, no noise</td><td>23.31</td><td>94.7%</td></tr>
            <tr><td>Non-random, .01 noise</td><td>16.33</td><td>70.8%</td></tr>
            <tr><td>Non-random, .02 noise</td><td>13.23</td><td>60.1%</td></tr>
            <tr><td>Non-random, .05 noise</td><td>-6.41</td><td>0.2%</td></tr>
            <tr><td>Random, no noise</td><td>-3.25</td><td>17.1%</td></tr>
            <tr><td>Random, .01 noise</td><td>-4.21</td><td>0.4%</td></tr>
          </tbody>
        </table>
      </Section>

      <Section title="Discussion">
        <p>
          As seen in the above table, the agent was able to succeed in the task when the goal position was constant, both with
          and without gaussian noise applied to the actions and observations. While I expected this from the no noise version,
          I was pleasantly surprised with the resilience of the with noise version. The ability to minimize error and return
          to the original trajectory was impressive.
        </p>
        <p>
          I was disappointed that the agent was unable to learn the task for random goal positions. While some random
          positions did work, the vast majority of goal positions left the agent unable to push the puck effectively. As seen
          from the training time graph, the reward stagnated with moderate reward. Currently I&apos;m not sure if more training
          time will get past this plateau, but based on the shape of the learning curve for non-random environments, it seems
          unlikely.
        </p>
      </Section>

      <Section title="Future Work">
        <p>
          Solving this task can be expanded upon in a number of different ways. I only used PPO for this project, as
          preliminary attempts with other algorithms didn&apos;t work. Other algorithms may help improve efficiency or allow for more
          generalization when it comes to goal position. Aside from other algorithms, the environment itself has room for
          improvement. I made some minor optimizations to actions, observations, and rewards, but there is room for growth
          there. For example, my agents tended to favor very small moves, taking a long time to complete the task. By
          increasing the reward loss for each timestep I can more effectively push the agent towards completing the task more
          quickly. This kind of reward balancing has the potential to significantly speed up the learning process.
        </p>
        <p>
          There is also more work than can be done on the topic of transfer learning. It is possible that some models can
          function effectively on environments that they weren&apos;t trained on. The most likely candidates for this would be the
          random-goal agents running on a static-goal environment.
        </p>
      </Section>

      <Section title="Physical Environment" figures={[{ src: setup, alt: 'ABB IRB 120 manipulator, board, mirror and camera setup' }]}>
        <p>
          The physical agent was implemented with an ABB IRB 120 robotic manipulator, with a pen attached to the end effector,
          pushing around a bottle cap (our puck). This &quot;cap&quot; was tracked by a raspberry pi camera to obtain its current
          position.
        </p>
      </Section>

      <Section title="Physical Agent Actions">
        <p>
          The position of the end effector was controlled using
          the <a href="https://gitlab.com/dartmouthrobotics/abb-basics">abb basics</a> library.
          This method starts a TCP server on the manipulator controller, allowing a python client to send position
          commands to the manipulator. After finding the bounds that the manipulator can move the end effector to, the
          manipulator can be moved to any coordinate position within these bounds. The displacement vector from the RL agent
          is converted into displacement in mm, then the manipulator is commanded to go to the specified position.
        </p>
      </Section>

      <Section title="Physical Agent Observations">
        <p>
          The observations for the physical environment require knowledge of the puck&apos;s position in the x and y dimensions.
          For simplicity&apos;s sake, I chose to implement this system with one raspberry pi camera module (alternatives will be
          reviewed in the below section). To get both the x and y positions of the cap, I utilized reflections. With a
          mirror position at a 45% angle, it is possible to view the puck both from the front and from the side. Utilizing
          a <a href="https://www.desmos.com/calculator/fhym9wu2xb">simple reflective mathematical model</a> I was able to predict the position of the puck using two observed
          angles.
        </p>
      </Section>

      <Section figures={[{ src: model, alt: 'reflective model of puck and its virtual image' }]} />

      <Section>
        <p>
          To determine the position of the puck, two angles are required. Specifically, the angle off center to the puck,
          and the angle off center to the virtual image of the puck. To find these angles, I followed these steps:
        </p>
        <ol>
          <li>Centered the image so the camera looks straight along the edge of the board</li>
          <li>Measured the distance of the camera to the board</li>
          <li>Captured an image, subsetting to capture only the board and puck</li>
          <li>For each cap, color-mask for just the color red</li>
          <li>Find the horizontal position of the median of these red pixels</li>
          <li>Convert the pixel distance from center to angle from center (assuming linear interpolation with respect to FOV)</li>
        </ol>
        <p>
          Using these steps allowed me to find the angle of the real and reflected puck. These angles can be plugged into the
          mathematical model (above) to find the correct position of the puck. Given the measured position of the puck, and
          known positions of goal and end effector it is then trivial to give the agent the observation vectors.
        </p>
      </Section>

      <Section title="Physical Environment Results">
        <p>
          Despite the ability of the mirror method to calculate x and y position from one camera, this method caused
          considerable errors in the real world. In a calibration exercise, the puck was attached to the end effector. This allowed me to
          move the end effector to designated coordinates, and measure the error between real and measured position.
        </p>
      </Section>

      <Section figures={[{ src: calibrate, alt: 'calibration run with the puck attached to the end effector' }]} />

      <Section>
        <p>
          As the cap moves to the side the angle to the real image changes significantly. When the cap moves
          away, the angle to the virtual angle changes significantly. While it showed the feasibility of the mirror method,
          this calibration also revealed significant errors.
        </p>
        <p>
          It was found that after many attempts to improve the calibration, the calculated positions were still off by wide
          margins in unpredictable ways.
        </p>
      </Section>

      <Section figures={[{ src: calibResults, alt: 'real vs. measured puck positions from calibration' }]} />

      <Section>
        <p>
          Despite my best efforts, the calculated position is regularly incorrect. There is a broader trend,
          where points tend to be distorted to the left (-X). Even accounting for this trend, we can see observation error
          that appears random, there is not easily observable trend for it. Even subsequent measurements of the same position
          would sometimes yield different observations.
        </p>
        <p>
          I decided that for the task to be even remotely possible, the observation error should not surpass 10mm. If the error were to
          surpass that, it would be impossible, even for a perfect agent to complete the task. After multiple attempts to
          reduce the error, I found the random-appearing observation error to be well over 10mm, making the task impossible to
          execute with this observation system. At this point, I dedicated my time to better understanding the sources of the
          error.
        </p>
      </Section>

      <Section title="Sources of Observation Error">
        <p>
          This observational error can be attributed to many sources. In attempting to find the x,y position of the puck from
          a single camera, any number of potential issues can cause compounding errors.
        </p>
        <p>
          First, the physical setup of the camera and its position is prone to human error. The mirror was placed at 45%, and
          the camera was positioned with the center parallel to the board edge. Both of these were only validated by eye, and
          errors here would cause significant offset in the angles later on. While I can guarantee that the angles looked
          correct, I do not have measurements attesting to the fact.
        </p>
        <p>
          Secondly, the process of finding the pixel positions of the puck had its own issues. The colormask caused issues
          as red items in the background could cause the median red pixel to stray away from the puck. Similarly, lighting
          conditions could cause parts of the puck to not be color masked correctly, leading to more error. Additionally, the
          pen obstructing a part of the puck could cause the median pixel to be misplaced.
        </p>
        <p>
          Third, for the simplicity of the process I assumed a linear interpolation of angle from the field of view. That is,
          I calculated the percent distance from the center to the edge and multiplied it by half the FOV angle for the
          camera. This is guaranteed to produce error, as all cameras experience distortion, so a linear interpolation is bound to fail.
        </p>
      </Section>

      <Section title="Further Work">
        <p>
          If I spent more time with this camera setup, each of these three above sources of error could be reduced. The
          physical camera setup could be more carefully measured. A consistent lighting setup with a monochromatic background
          would increase the efficacy of the color masking significantly. Finally, calculating and correcting the camera
          distortion to more accurately calculate angle values from pixel values would significantly reduce error from
          distortion.
        </p>
        <p>
          As opposed to using the mirror camera setup, other alternative observational systems can be explored. Other camera
          options, such as viewing from above, or two cameras could simplify the calculations. Additionally, the puck could be
          altered such that discerning its position is easier, such as adding fiducial markers to it. Other sensing options
          are available, such as using LIDAR or other depth-sensing equipment.
        </p>
      </Section>

      <Section>
        <p>The original course report (COSC 094) is <a href="https://puckreport.netlify.app/">here</a>.</p>
      </Section>
    </>
  );
};

export default RLRobot;
